import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pill, MapPin, LineChart, ArrowRight, Sparkles, TrendingDown, Users, MessagesSquare, Bell, BookOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DisclaimerBanner } from "../components/DisclaimerBanner";
import { CheckinPrompt } from "../components/CheckinPrompt";
import { SecureAccountCard } from "../components/SecureAccountCard";
import { ContactBlock } from "../components/ContactBlock";
import api from "../lib/api";

const tiles = [
  { to: "/app/taper", icon: Pill, title: "Taper Tool", desc: "Reference dosing & GP letter", color: "#4A7c59" },
  { to: "/app/locator", icon: MapPin, title: "Find Support", desc: "Recovery centres near you", color: "#8FB8DE" },
  { to: "/app/tracker", icon: LineChart, title: "Progress", desc: "Log your day & see trends", color: "#E8907E" },
  { to: "/app/reminders", icon: Bell, title: "Reminders", desc: "Gentle dose & journal nudges", color: "#4A7c59" },
  { to: "/app/buddy", icon: Users, title: "Buddy", desc: "Share progress with someone you trust", color: "#8FB8DE" },
  { to: "/app/community", icon: MessagesSquare, title: "Community", desc: "Peer support boards", color: "#E8907E" },
  { to: "/app/learn", icon: BookOpen, title: "Learn", desc: "Guides, articles & FAQs", color: "#4A7c59" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);

  useEffect(() => { api.get("/logs").then(({ data }) => setM(data.milestones)).catch(() => {}); }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <p className="text-sm uppercase tracking-[0.1em] text-muted-foreground">{greet}</p>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Welcome back, {user?.display_name} 🌿</h1>
        <p className="mt-2 text-muted-foreground">Every step you take here matters. Let's keep going, gently.</p>
      </div>

      <CheckinPrompt />
      <SecureAccountCard />

      {m && m.days_tracked > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkles className="h-4 w-4 text-[#4A7c59]" /> Days tracked</div>
            <div className="mt-2 font-head text-4xl font-bold text-[#4A7c59]" data-testid="stat-days">{m.days_tracked}</div>
            <p className="mt-1 text-xs text-muted-foreground">You're still further along than day one.</p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingDown className="h-4 w-4 text-[#8FB8DE]" /> Dose reduced</div>
            <div className="mt-2 font-head text-4xl font-bold text-[#2C4763]" data-testid="stat-reduction">{m.total_reduction > 0 ? m.total_reduction : 0}<span className="text-lg"> mg</span></div>
            <p className="mt-1 text-xs text-muted-foreground">{m.percent_reduced > 0 ? `${m.percent_reduced}% down from where you started.` : "Progress isn't always linear — that's okay."}</p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><LineChart className="h-4 w-4 text-[#E8907E]" /> Journey length</div>
            <div className="mt-2 font-head text-4xl font-bold text-[#8a4436]" data-testid="stat-journey">{m.days_since_start}<span className="text-lg"> days</span></div>
            <p className="mt-1 text-xs text-muted-foreground">Since you started tracking.</p>
          </div>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} data-testid={`tile-${t.title.toLowerCase().replace(/\s/g, "-")}`}
            className="group rounded-2xl border border-border bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `${t.color}1a`, color: t.color }}><t.icon strokeWidth={1.8} className="h-6 w-6" /></div>
            <h3 className="font-head text-lg font-semibold text-[#2C363F]">{t.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
            <div className="mt-3 flex items-center gap-1 text-sm font-medium text-[#4A7c59] opacity-0 transition-opacity duration-300 group-hover:opacity-100">Open <ArrowRight className="h-4 w-4" /></div>
          </Link>
        ))}
      </div>

      <ContactBlock />

      <DisclaimerBanner />
    </div>
  );
}
